import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DataDeletion() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Ange e-postadress");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/data-deletion/request`, {
        email,
        reason,
        source: "user_form",
      });
      setConfirmationCode(data.confirmation_code);
      toast.success("Begäran mottagen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skicka begäran");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="data-deletion-page">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="deletion-home-link">
            <span className="h-7 w-7 rounded bg-brand flex items-center justify-center font-heading text-xs font-bold text-white">
              T
            </span>
            <span className="font-heading text-base text-slate-900">Tidsrapporten</span>
          </Link>
          <Link to="/privacy" className="text-sm text-brand hover:underline" data-testid="deletion-to-privacy-link">
            Integritetspolicy →
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900">
          Radera mina data
        </h1>
        <p className="mt-4 text-base text-slate-600">
          Fyll i formuläret nedan så raderar vi dina personuppgifter från Tidsrapporten inom
          30 dagar, i enlighet med GDPR artikel 17 (”rätten att bli bortglömd”) samt Meta
          Platform Terms.
        </p>

        <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>Viktigt:</strong> Vissa bokförings­uppgifter (fakturor, tidrapporter,
          personalliggare) måste enligt svensk lag sparas i upp till 7 år och kan inte raderas
          förrän lagringstiden gått ut. Övriga data (namn, e-post, uppladdade filer utan
          juridisk grund) raderas omedelbart.
        </div>

        {confirmationCode ? (
          <div
            className="mt-10 rounded-lg border border-emerald-200 bg-emerald-50 p-6"
            data-testid="deletion-confirmation"
          >
            <h2 className="text-lg font-semibold text-emerald-900">Begäran mottagen</h2>
            <p className="mt-2 text-sm text-emerald-800">
              Vi har tagit emot din begäran och skickar bekräftelse till <strong>{email}</strong>{" "}
              inom kort.
            </p>
            <p className="mt-4 text-sm text-emerald-800">
              Ditt bekräftelse-ID (spara denna):
            </p>
            <div
              className="mt-2 font-mono text-lg font-bold text-emerald-900 bg-white rounded p-3 border border-emerald-200"
              data-testid="deletion-confirmation-code"
            >
              {confirmationCode}
            </div>
            <p className="mt-4 text-xs text-emerald-700">
              Kontrollera din begäran-status genom att mejla{" "}
              <a href={`mailto:paul@trappkonsulten.se?subject=Data%20deletion%20status%20${confirmationCode}`} className="underline">
                paul@trappkonsulten.se
              </a>{" "}
              med detta ID.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="deletion-form">
            <div>
              <label className="block text-sm font-medium text-slate-900">E-post kopplad till kontot *</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:ring-brand"
                placeholder="namn@företag.se"
                data-testid="deletion-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-900">
                Anledning (frivilligt)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:ring-brand"
                placeholder="T.ex. jag använder inte längre tjänsten"
                data-testid="deletion-reason-input"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-brand px-6 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              data-testid="deletion-submit-btn"
            >
              {submitting ? "Skickar..." : "Skicka begäran"}
            </button>
          </form>
        )}

        <section className="mt-16 space-y-4 text-sm text-slate-600">
          <h2 className="text-lg font-semibold text-slate-900">Så här går processen till</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Vi tar emot din begäran och skickar bekräftelse via e-post.</li>
            <li>Vi verifierar din identitet (endast kontoägare kan begära radering).</li>
            <li>Icke-lagringspliktig data raderas inom 30 dagar.</li>
            <li>Bokföringspliktig data raderas automatiskt när lagringstiden löper ut (upp till 7 år).</li>
            <li>Du får ett slutgiltigt bekräftelsemejl när raderingen är klar.</li>
          </ol>
        </section>

        <section className="mt-12 rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
          <strong className="text-slate-900">Meta / Instagram-användare:</strong> Om du kommit
          hit via en Instagram- eller Facebook-post från oss, notera att vi endast publicerar
          från vårt eget företagskonto. Vi lagrar inga personuppgifter om dig från Meta. Din
          begäran ovan raderar endast eventuella data om du varit registrerad kund hos oss.
        </section>
      </main>

      <footer className="border-t border-slate-200 mt-16">
        <div className="mx-auto max-w-4xl px-6 py-6 text-xs text-slate-500 flex justify-between">
          <span>© {new Date().getFullYear()} Tidsrapporten</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-slate-900">Integritetspolicy</Link>
            <Link to="/data-deletion" className="hover:text-slate-900">Data­radering</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
